import React, { useState } from "react";
import { Check, BadgePercent, Star } from "lucide-react";
import { TOWING_PROMO, normalPrice, packagePrice, saving, money } from "../../data/promo";

export default function PackageComparison({ tier: controlled, onTierChange }) {
  const [inner, setInner] = useState("smart");
  const tier = controlled ?? inner;
  const setTier = (k) => { setInner(k); onTierChange?.(k); };
  const t = TOWING_PROMO.tiers.find((x) => x.key === tier);
  const rows = [...TOWING_PROMO.packages].sort((a, b) => (a.price.smart ?? 0) - (b.price.smart ?? 0));

  return (
    <div data-testid="package-comparison">
      <p className="bebas text-white/50 tracking-[0.25em] text-xs mb-3">STEP 1 — PICK YOUR VEHICLE'S TOW-PRO TIER</p>
      <div className="flex flex-wrap gap-2 mb-3" role="tablist">
        {TOWING_PROMO.tiers.map((x) => (
          <button
            key={x.key}
            type="button"
            role="tab"
            aria-selected={tier === x.key}
            onClick={() => setTier(x.key)}
            data-testid={`tier-tab-${x.key}`}
            className={`bebas tracking-[0.15em] text-base md:text-lg px-5 py-3 border transition-colors ${
              tier === x.key ? "bg-cyan-pz text-black border-cyan-pz" : "border-white/20 text-white/70 hover:border-cyan-pz hover:text-cyan-pz"
            }`}
          >
            {x.label}
          </button>
        ))}
      </div>
      <p data-testid="tier-vehicles" className="text-white/55 barlow text-sm mb-8">{t.vehicles}</p>

      <p className="bebas text-white/50 tracking-[0.25em] text-xs mb-3">STEP 2 — PICK YOUR PACKAGE · THE MORE YOU BUNDLE, THE MORE YOU SAVE</p>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[720px] text-left">
          <thead className="bg-[#0A0A0A]">
            <tr className="bebas tracking-[0.2em] text-xs text-white/50">
              <th className="px-5 py-4 font-normal">PACKAGE</th>
              <th className="px-5 py-4 font-normal whitespace-nowrap">NORMAL PRICE</th>
              <th className="px-5 py-4 font-normal whitespace-nowrap text-cyan-pz">PACKAGE PRICE</th>
              <th className="px-5 py-4 font-normal whitespace-nowrap">YOU SAVE</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {rows.map((pkg) => {
              const price = packagePrice(pkg, tier);
              const save = saving(pkg, tier);
              return (
                <tr key={pkg.key} data-testid={`package-row-${pkg.key}`} className={`bg-black ${pkg.popular ? "bg-cyan-pz/5" : ""}`}>
                  <td className="px-5 py-5 align-top">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="bebas text-white text-xl md:text-2xl leading-tight">{pkg.name}</p>
                      {pkg.popular && (
                        <span className="inline-flex items-center gap-1 bg-cyan-pz text-black px-2 py-0.5 bebas tracking-[0.2em] text-[10px] clip-tag">
                          <Star className="w-3 h-3" /> MOST POPULAR
                        </span>
                      )}
                    </div>
                    <ul className="mt-2 space-y-1">
                      {pkg.includes.map((i) => (
                        <li key={i} className="flex items-start gap-2 text-white/60 barlow text-xs md:text-sm"><Check className="w-3.5 h-3.5 text-cyan-pz mt-0.5 shrink-0" />{i}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-5 py-5 align-top whitespace-nowrap">
                    <span data-testid={`normal-${pkg.key}`} className="bebas text-white/45 text-xl line-through decoration-white/40">{money(normalPrice(pkg, tier))}</span>
                  </td>
                  <td className="px-5 py-5 align-top whitespace-nowrap">
                    {price == null ? (
                      <span data-testid={`price-${pkg.key}`} className="bebas text-white/70 text-lg">Quoted on request</span>
                    ) : (
                      <span data-testid={`price-${pkg.key}`} className="bebas text-cyan-pz text-3xl md:text-4xl leading-none">from {money(price)}</span>
                    )}
                  </td>
                  <td className="px-5 py-5 align-top whitespace-nowrap">
                    {save != null && (
                      <span data-testid={`save-${pkg.key}`} className="inline-flex items-center gap-1.5 bg-white text-black px-3 py-1.5 bebas tracking-[0.15em] text-base clip-tag">
                        <BadgePercent className="w-4 h-4" /> SAVE {money(save)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
